/**
 * NotificationsBottomSheet — mobile bottom sheet for the notification center (WI-56).
 *
 * Renders at 60% viewport height. Reuses the BottomSheet primitive (Radix Dialog)
 * for focus trap, Escape key close, and aria-modal semantics.
 *
 * Differences from NotificationsDropdown:
 *   - Fixed to bottom of viewport (60vh height)
 *   - Row min-height is min-h-[56px] for touch targets
 *   - No "View older" footer on mobile (screen-space preservation)
 *   - Drag handle rendered by BottomSheet primitive
 */

import React from 'react';
import { NotificationRow } from './NotificationRow';
import { NotificationRowSkeleton } from './NotificationRowSkeleton';
import type { NotificationItem } from '../api/notifications';

interface Props {
  isOpen: boolean;
  notifications: NotificationItem[];
  isLoading: boolean;
  onClose: () => void;
  onNotificationClick: (n: NotificationItem) => void;
}

export function NotificationsBottomSheet({
  isOpen,
  notifications,
  isLoading,
  onClose,
  onNotificationClick,
}: Props) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-label="Notifications"
        aria-modal="true"
        aria-busy={isLoading ? 'true' : 'false'}
        className="fixed inset-x-0 bottom-0 z-50 rounded-t-2xl bg-white shadow-xl flex flex-col"
        style={{ height: '60vh' }}
      >
        {/* Drag handle — decorative */}
        <div className="flex justify-center pt-3 pb-1 flex-shrink-0" aria-hidden="true">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-hairline flex-shrink-0">
          <span className="text-sm font-semibold text-gray-900">Notifications</span>
          <button
            type="button"
            aria-label="Close notifications"
            onClick={onClose}
            className="w-11 h-11 flex items-center justify-center rounded text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body — scrollable */}
        <div
          className="overflow-y-auto flex-1"
          role="list"
          aria-label="Notification items"
          style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
        >
          {isLoading ? (
            <>
              <NotificationRowSkeleton />
              <NotificationRowSkeleton />
              <NotificationRowSkeleton />
            </>
          ) : notifications.length === 0 ? (
            <MobileEmptyState />
          ) : (
            notifications.map((n) => (
              <MobileNotificationRow
                key={n.id}
                notification={n}
                onClick={onNotificationClick}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
}

/**
 * Mobile variant of NotificationRow with min-h-[56px] for touch targets.
 */
function MobileNotificationRow({
  notification: n,
  onClick,
}: {
  notification: NotificationItem;
  onClick: (n: NotificationItem) => void;
}) {
  return (
    <div className="min-h-[56px] flex">
      <NotificationRow notification={n} onClick={onClick} />
    </div>
  );
}

function MobileEmptyState() {
  return (
    <div className="px-6 py-8 flex flex-col items-center text-center gap-3">
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-gray-300"
        aria-hidden="true"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
      <p className="text-sm text-gray-600 leading-relaxed">
        No notifications yet. We&rsquo;ll alert you when your saved schools&rsquo; scores change
        or new ID camps are added.
      </p>
    </div>
  );
}
